import { useId, useState, type FormEvent, type ReactElement } from 'react';
import {
  StravaAppConfigPutBody,
  StravaAppConfigValidationError,
  useUpdateStravaAppConfig,
  type StravaAppConfigPutResponse,
} from '@/lib/strava';

/**
 * Formulario para configurar la app Strava del profile (Client ID + Client
 * Secret). Renderizado en dos escenarios:
 *
 *  - Estado 1 inicial: el usuario aún no ha configurado claves.
 *  - Modo "editar" desde estado 2/3: se precarga el `clientId` actual; el
 *    secret SIEMPRE debe re-introducirse porque el backend nunca lo devuelve.
 *
 * Decisión UX: NO permitimos enviar sólo el clientId. El contrato exige
 * `{ clientId, clientSecret }` siempre, y por seguridad (cifrado AES-GCM)
 * no podemos asumir el secret previo. Avisamos arriba de que editar invalida
 * la conexión OAuth.
 */
export function StravaAppConfigForm({
  callbackUrl,
  initialClientId,
  mode = 'create',
  onSaved,
  onCancel,
}: {
  callbackUrl: string;
  initialClientId?: string;
  mode?: 'create' | 'edit';
  onSaved?: (response: StravaAppConfigPutResponse) => void;
  onCancel?: () => void;
}): ReactElement {
  const clientIdId = useId();
  const clientSecretId = useId();
  const callbackUrlId = useId();
  const instructionsId = useId();
  const formErrorsId = useId();

  const [clientId, setClientId] = useState(initialClientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(mode === 'create');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  // Errores de validación cliente (Zod local) + errores devueltos por el backend.
  const [clientFieldErrors, setClientFieldErrors] = useState<{
    clientId?: string;
    clientSecret?: string;
  }>({});

  const mutation = useUpdateStravaAppConfig();

  const serverValidation =
    mutation.error instanceof StravaAppConfigValidationError ? mutation.error : null;
  const otherError =
    mutation.error && !serverValidation
      ? mutation.error
      : null;

  function fieldError(field: 'clientId' | 'clientSecret'): string | undefined {
    if (clientFieldErrors[field]) return clientFieldErrors[field];
    const fromServer = serverValidation?.fieldErrors[field]?.[0];
    return fromServer;
  }

  async function handleCopyCallback() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(callbackUrl);
        setCopyState('copied');
        window.setTimeout(() => setCopyState('idle'), 2000);
      } else {
        setCopyState('error');
      }
    } catch {
      setCopyState('error');
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Reset estado previo de validación cliente.
    setClientFieldErrors({});

    const parsed = StravaAppConfigPutBody.safeParse({ clientId, clientSecret });
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      setClientFieldErrors({
        clientId: flat.fieldErrors.clientId?.[0],
        clientSecret: flat.fieldErrors.clientSecret?.[0],
      });
      return;
    }

    mutation.mutate(parsed.data, {
      onSuccess: (response) => {
        // Limpiamos secret para que no quede en memoria del input ni se
        // re-envíe si el form sigue montado.
        setClientSecret('');
        setShowSecret(false);
        if (onSaved) onSaved(response);
      },
    });
  }

  return (
    <section
      aria-labelledby={`${clientIdId}-title`}
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id={`${clientIdId}-title`}
        className="text-lg font-semibold text-slate-900"
      >
        {mode === 'edit' ? 'Editar claves de la app Strava' : 'Configura tu app de Strava'}
      </h2>

      <p className="mt-2 text-sm text-slate-700">
        tri-mentor necesita las claves de tu propia app de Strava para
        sincronizar tus actividades. Esto es un paso único y las claves se
        guardan cifradas.
      </p>

      {mode === 'edit' && (
        <p
          role="note"
          className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          Cambiar el Client Secret invalida tu conexión OAuth actual con
          Strava. Tendrás que volver a pulsar &laquo;Conectar Strava&raquo;
          después.
        </p>
      )}

      {/* Instrucciones colapsables */}
      <div className="mt-4">
        <button
          type="button"
          aria-expanded={instructionsOpen}
          aria-controls={instructionsId}
          onClick={() => setInstructionsOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 rounded"
        >
          <span aria-hidden="true">{instructionsOpen ? '▾' : '▸'}</span>
          {instructionsOpen ? 'Ocultar instrucciones' : 'Cómo conseguir tus claves'}
        </button>

        {instructionsOpen && (
          <ol
            id={instructionsId}
            className="mt-3 list-decimal space-y-2 pl-6 text-sm text-slate-700"
          >
            <li>
              Abre{' '}
              <a
                href="https://www.strava.com/settings/api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 underline hover:text-teal-800"
              >
                strava.com/settings/api
              </a>{' '}
              e inicia sesión.
            </li>
            <li>
              En <strong>Create &amp; Manage Your App</strong>, completa:
              <ul className="mt-1 list-disc space-y-1 pl-6">
                <li>
                  <strong>Application Name:</strong> p.ej.{' '}
                  <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                    tri-mentor (mi instancia local)
                  </code>
                </li>
                <li>
                  <strong>Category:</strong>{' '}
                  <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                    Training
                  </code>
                </li>
                <li>
                  <strong>Website:</strong>{' '}
                  <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                    http://localhost:3000
                  </code>
                </li>
                <li>
                  <strong>Authorization Callback Domain:</strong>{' '}
                  <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                    localhost
                  </code>
                </li>
              </ul>
            </li>
            <li>
              Copia el <strong>Client ID</strong> y el{' '}
              <strong>Client Secret</strong> en los campos de abajo.
            </li>
          </ol>
        )}
      </div>

      {/* Callback URL — informativo, para que el usuario lo pegue en Strava */}
      <div className="mt-4">
        <label
          htmlFor={callbackUrlId}
          className="block text-sm font-medium text-slate-800"
        >
          Callback URL (referencia)
        </label>
        <p className="mt-1 text-xs text-slate-600">
          En Strava sólo necesitas indicar el <em>domain</em>{' '}
          (<code className="rounded bg-slate-100 px-1 py-0.5">localhost</code>),
          pero esta es la URL completa que recibe el backend.
        </p>
        <div className="mt-1 flex items-stretch gap-2">
          <input
            id={callbackUrlId}
            type="text"
            value={callbackUrl}
            readOnly
            className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-600"
          />
          <button
            type="button"
            onClick={handleCopyCallback}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            {copyState === 'copied'
              ? 'Copiado'
              : copyState === 'error'
                ? 'Copia manual'
                : 'Copiar'}
          </button>
        </div>
        {copyState === 'error' && (
          <p role="status" className="mt-1 text-xs text-amber-800">
            Tu navegador no permite copiar al portapapeles. Selecciona y copia
            el texto manualmente.
          </p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
        {/* Client ID */}
        <div>
          <label
            htmlFor={clientIdId}
            className="block text-sm font-medium text-slate-800"
          >
            Client ID
          </label>
          <input
            id={clientIdId}
            name="clientId"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            required
            value={clientId}
            onChange={(e) => setClientId(e.target.value.trim())}
            aria-invalid={fieldError('clientId') ? true : undefined}
            aria-describedby={
              fieldError('clientId') ? `${clientIdId}-err` : undefined
            }
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
            placeholder="12345"
          />
          {fieldError('clientId') && (
            <p
              id={`${clientIdId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('clientId')}
            </p>
          )}
        </div>

        {/* Client Secret */}
        <div>
          <label
            htmlFor={clientSecretId}
            className="block text-sm font-medium text-slate-800"
          >
            Client Secret
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              id={clientSecretId}
              name="clientSecret"
              type={showSecret ? 'text' : 'password'}
              autoComplete="off"
              spellCheck={false}
              required
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value.trim())}
              aria-invalid={fieldError('clientSecret') ? true : undefined}
              aria-describedby={
                fieldError('clientSecret') ? `${clientSecretId}-err` : undefined
              }
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
              placeholder={mode === 'edit' ? 'Vuelve a pegar tu Client Secret' : ''}
            />
            <button
              type="button"
              aria-pressed={showSecret}
              aria-label={showSecret ? 'Ocultar Client Secret' : 'Mostrar Client Secret'}
              onClick={() => setShowSecret((v) => !v)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
            >
              {showSecret ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {fieldError('clientSecret') && (
            <p
              id={`${clientSecretId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('clientSecret')}
            </p>
          )}
        </div>

        {/* Errores generales (formErrors o errores no de validación) */}
        {(serverValidation?.formErrors.length || otherError) && (
          <div
            id={formErrorsId}
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            {serverValidation?.formErrors.length
              ? serverValidation.formErrors.join(' ')
              : otherError?.message ?? 'No se pudieron guardar las claves.'}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={mutation.isPending}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
